/**
 * FirstUseClientPicker — C8 wizard refactor
 *
 * Se muestra UNA SOLA VEZ cuando:
 *   - El usuario tiene >1 client accesible
 *   - No tiene last_client_id guardado (datos legacy o primer login)
 *
 * NO es una pantalla bloqueante separada — se renderiza sobre el layout
 * normal como un overlay modal. Una vez elegido, el ClientContext persiste
 * la elección vía switch → last_client_id se guarda en DB.
 *
 * IMPORTANTE: Este componente NO reemplaza la ruta /tenant-selection.
 * La ruta /tenant-selection se elimina del router (App.js).
 */

import React, { useState } from 'react';
import {
  Buildings as Building2,
  ArrowRight,
  SpinnerGap as Loader2,
  WarningCircle as AlertCircle,
} from '@phosphor-icons/react';
import { useClient } from '@/contexts/ClientContext';

const FirstUseClientPicker = () => {
  const { clients, switchClient } = useClient();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSelect = async (clientId) => {
    setSelected(clientId);
    setLoading(true);
    setError('');
    try {
      await switchClient(clientId);
      // Tras el switch, ClientContext actualiza activeClientId
      // y needsClientSelection pasa a false → este componente desaparece
    } catch (err) {
      setError('No se pudo conectar al cliente. Intenta de nuevo.');
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-v4-bg border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06]">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400/80 mb-1">
            PRIMER ACCESO
          </p>
          <h2 className="text-lg font-semibold text-v4-txt tracking-tight">
            Selecciona un client
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Tu cuenta tiene acceso a varios clientes. Elige el que quieres gestionar ahora.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 flex items-center gap-2 p-3 bg-red-500/[0.06] border border-red-500/[0.25] rounded-md">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
            <span className="text-xs text-red-300">{error}</span>
          </div>
        )}

        {/* Client list */}
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
          {clients.map((client) => {
            const isSelected = selected === client.id;
            return (
              <button
                key={client.id}
                type="button"
                onClick={() => !loading && handleSelect(client.id)}
                disabled={loading}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all duration-200 ${
                  isSelected
                    ? 'border-cyan-500/[0.35] bg-cyan-500/[0.06]'
                    : 'border-white/[0.06] bg-slate-900/40 hover:border-cyan-500/[0.2] hover:bg-slate-900/60'
                } ${loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="w-9 h-9 rounded-md bg-slate-950/60 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                  <Building2 size={16} className="text-cyan-400" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-v4-txt truncate">
                    {client.name}
                  </div>
                  {client.industry && (
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">
                      {client.industry}
                    </div>
                  )}
                </div>
                {isSelected && loading ? (
                  <Loader2 size={14} className="text-cyan-400 animate-spin flex-shrink-0" />
                ) : (
                  <ArrowRight
                    size={14}
                    className="text-slate-600 group-hover:text-cyan-400 flex-shrink-0"
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t border-white/[0.06]">
          <p className="text-[10px] text-slate-600 font-mono">
            Puedes cambiar de client en cualquier momento desde el selector del topbar.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirstUseClientPicker;
